/* eslint-disable no-param-reassign */
import {
  Getters,
  Mutations,
  Actions,
  Module,
  createMapper,
} from 'vuex-smart-module';

import ApiFactory from '@/api/apiFactory';

const MenuRepository = ApiFactory.get('menu');

interface ImenuItem {
  label: string;
  items: [];
  numbered: boolean;
  type: string;
  url: string;
  parent?: string;
  order?: number;
  lvl?: number;
  expanded?: boolean;
  generatedNumber?: string;
  hasContent: boolean;
}
interface IfloatMenuItem {
  label: string;
  lvl: number;
  order: number;
  numbered: boolean;
  parent: string;
  type: string;
  url: string;
  expanded: boolean;
  generatedNumber?: string;
  hasContent: boolean;
}

const treeWalker = ((tree: any, url: string) => {
  const selected = tree.find((item: IfloatMenuItem) => item.url === url);
  if (selected) {
    (selected as IfloatMenuItem).expanded = true;
    if ((selected as IfloatMenuItem).parent !== 'root') {
      const parent = tree.find((item: IfloatMenuItem) => item.label === selected.parent);
      treeWalker(tree, parent.url);
    }
  }
});

const calcTreeNumberPosition = (tree: Array<IfloatMenuItem>, item: IfloatMenuItem): Array<number> => {
  let orderArray = [];
  orderArray.push(item.order + 1);
  if (item.parent && item.parent !== 'root') {
    const parent = tree.find((search: IfloatMenuItem) => search.label === item.parent);
    if (parent) {
      orderArray = orderArray.concat(calcTreeNumberPosition(tree, parent));
    }
  }
  return orderArray;
};

const calcMenuParentsArray = (tree: Array<IfloatMenuItem>, item: IfloatMenuItem): Array<string> => {
  let parentArray = [];
  parentArray.push(item.label);
  if (item.parent && item.parent !== 'root') {
    const parent = tree.find((search: IfloatMenuItem) => search.label === item.parent);
    if (parent) {
      parentArray = parentArray.concat(calcMenuParentsArray(tree, parent));
    }
  }
  return parentArray;
};

const menuFloater = (items: Array<ImenuItem>, parent: string, lvl: number, generatedNumber?: string): Array<IfloatMenuItem> => {
  let floatMenu: any[] = [];
  let order = 0;
  const tempItemsArray = items;
  tempItemsArray.forEach((item: ImenuItem) => {
    item.parent = parent;
    item.order = order;
    item.expanded = false;
    item.lvl = lvl;
    order += 1;
    if (item.numbered) {
      item.generatedNumber = `${generatedNumber || ''}${order}`;
    }
    if (item.items && item.items.length) {
      floatMenu = floatMenu.concat(menuFloater(item.items, item.label, lvl + 1, item.numbered ? `${item.generatedNumber}.` : undefined));
    }
    delete item.items;
    floatMenu.push(item);
  });
  return floatMenu;
};

const keyExtracter = (arr: Array<ImenuItem>) => {
  const result: Array<string> = [];
  arr.forEach((element) => {
    result.push(`${element.label}.header`);
    if (element.items && element.items.length) {
      result.push(...keyExtracter(element.items));
    }
  });
  return result;
};

class MenuState {
  tree = [];

  isLoaded = false;
}

class MenuGetters extends Getters<MenuState> {
  get tree() {
    return (type: string) => this.state.tree.filter((item: IfloatMenuItem) => item.type === type);
  }

  get currentMenuItem(): string {
    const menuItem = (this.state.tree as any).find((item: IfloatMenuItem) => !!item.expanded) || {};
    return menuItem.generatedNumber || '';
  }

  get menuItemNumber() {
    return (url: string) => {
      const menuItem = (this.state.tree as any).find((item: IfloatMenuItem) => item.url === url) || {};
      return menuItem.generatedNumber || '';
    };
  }

  // eslint-disable-next-line class-methods-use-this
  get menuNumberPosition() {
    return (label: string) => {
      const origin = this.state.tree.find((item: IfloatMenuItem) => item.label === label);
      return origin ? calcTreeNumberPosition(this.state.tree, origin).reverse().join('.') : '';
    };
  }

  get itemsList() {
    return (parent: string, type: string) => this.state.tree
      .filter((item: IfloatMenuItem) => item.parent === parent && item.type === type)
      .sort((a: IfloatMenuItem, b: IfloatMenuItem) => {
        if (a.order < b.order) {
          return -1;
        }
        if (a.order > b.order) {
          return 1;
        }
        return 0;
      });
  }

  get menuIsLoaded() {
    return this.state.isLoaded;
  }

  get parentExpanded() {
    return (label: string): boolean => {
      let result = false;
      const menuItem = (this.state.tree
        .find((item: IfloatMenuItem) => item.label === label));
      if (menuItem) {
        const menuItemLabel = (menuItem as IfloatMenuItem).parent;
        result = !!(this.state.tree
          .filter((item: IfloatMenuItem) => item.label === menuItemLabel && item.expanded).length);
      }
      return result;
    };
  }

  get nestedChildsCount() {
    return (parent: string): number => this.state.tree
      .filter((item: IfloatMenuItem) => item.parent === parent).length;
  }

  get expandedLvl() {
    return this.state.tree.filter((item: IfloatMenuItem) => !!item.expanded)
      .filter((item: IfloatMenuItem) => this.state.tree.find((subitem: IfloatMenuItem) => subitem.parent === item.label))
      .sort((a: IfloatMenuItem, b: IfloatMenuItem) => {
        if (a.lvl < b.lvl) {
          return -1;
        }
        if (a.lvl > b.lvl) {
          return 1;
        }
        return 0;
      })
      .map((item: IfloatMenuItem) => item.lvl)
      .pop() || 0;
  }

  get approvedForShowFooterMenuTree(): string | null {
    const freshTree: ImenuItem[] = [...this.state.tree];
    const result = freshTree.find(({ label }) => label === 'help.responsible_gambling');
    if (result) {
      return result.url;
    }

    return null;
  }
}

class MenuMutations extends Mutations<MenuState> {
  update(payload: any) {
    this.state.tree = [].concat(payload);
  }

  setMenuState(state: boolean) {
    this.state.isLoaded = state;
  }

  toggleItemExpanded(payload: any) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const origin: IfloatMenuItem = this.state.tree.find((item: IfloatMenuItem) => item.label === payload.label)!;
    const filtered = this.state.tree.filter((item: IfloatMenuItem) => calcMenuParentsArray(this.state.tree, origin).indexOf(item.label) === -1);
    const childs = this.state.tree.filter((item: IfloatMenuItem) => item.parent === origin.label).length;
    if (filtered && payload.state) {
      filtered.forEach((item: IfloatMenuItem) => {
        item.expanded = false;
      });
    }
    if (origin) {
      if (!(!childs && !payload.state)) {
        (origin as IfloatMenuItem).expanded = payload.state;
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  setActive(url: string) {
    this.state.tree.forEach((item: IfloatMenuItem) => {
      item.expanded = false;
    });
    treeWalker(this.state.tree, url);
  }

  allItemClose() {
    this.state.tree.forEach((item: IfloatMenuItem) => {
      item.expanded = false;
    });
  }
}

class MenuActions extends Actions<
  MenuState,
  MenuGetters,
  MenuMutations,
  MenuActions
> {
  async getAsyncData(code: string) {
    return new Promise((resolve, reject) => {
      MenuRepository.get(code)
        .then((menu) => {
          const keys = keyExtracter(menu);
          menu.forEach((menuItem: string, menuIndex: string) => {
            menu[menuIndex].expanded = false;
          });
          this.commit('update', menuFloater(menu, 'root', 1));
          resolve(keys);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }
}

// Экспорт модуля
export const MenuModule = new Module({
  state: MenuState,
  getters: MenuGetters,
  mutations: MenuMutations,
  actions: MenuActions,
});

export const MenuModuleMapper = createMapper(MenuModule);
